'use client';

import React, { useMemo, ReactNode, useEffect, useState } from 'react';
import {
  BookingSearchModule,
  useClientPersistSearchParams,
  DateDisplay,
  useAuth,
  useConfig,
  useAppService
} from '@ibe/components';
import { usePathname, useRouter } from 'next/navigation';
import useQuery from '@/Util/useQuery';
import isClient from '@/Util/globals';
import { BookingParamsTransformer } from '@/Params/BookingParamsTransformer';
import { ApiBookingPane, ApiBookingSearch } from '@ibe/api';
import { useTranslation } from '@/app/i18n/client';
import { getLinkUrl } from '../linkList/Link';
import { Language, languages } from '@/app/i18n/settingsClient';

export const SS_BOOKING_SEARCH_PARAMS_KEY = 'bookingSearchParams';

interface Props {
  bookingDetailsLink?: string;
}
const BookingList = ({ bookingDetailsLink: bookingDetailsLinkProps }: Props) => {
  const { t } = useTranslation('booking-list');

  const query = useQuery();
  const router = useRouter();
  const pathname = usePathname();
  const auth = useAuth();
  const appService = useAppService();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const config = useConfig();
  const { getSearchParams, setSearchParams } =
    useClientPersistSearchParams<Partial<ApiBookingSearch>>(isClient() ? window || null : null) ||
    {};

  const bookingDetailsLink = useMemo(() => {
    const path = bookingDetailsLinkProps ?? '';
    if (path.startsWith('http') || path.startsWith('https')) {
      return path;
    }
    const firstSegment = path.split('/')[0] as Language;
    return languages.includes(firstSegment) ? path : `/${appService.lang}${path}`;
  }, [bookingDetailsLinkProps, appService.lang]);

  useEffect(() => {
    setIsLoggedIn(auth.authState.isLoggedIn);
  }, [auth.authService.isLoggedIn]);

  const updateUrlParams = (params: Partial<ApiBookingSearch>): void => {
    // set ss item, so when returning to list from BookingDetails, the same list is shown
    sessionStorage.setItem(SS_BOOKING_SEARCH_PARAMS_KEY, JSON.stringify(params));

    setSearchParams(
      pathname,
      (({
        bookingDateStart,
        bookingDateEnd,
        travelDateStart,
        travelDateEnd
      }): Partial<ApiBookingSearch> => ({
        bookingDateStart,
        bookingDateEnd,
        travelDateStart,
        travelDateEnd
      }))(params)
    );
    const urlParams = BookingParamsTransformer.encode(params);
    router.push(`${pathname}?${urlParams}${urlParams && '&'}`);
  };

  const searchParams = useMemo(() => {
    const params = getSearchParams ? getSearchParams(pathname) : undefined;
    return Object.keys(Object.fromEntries(query.entries())).length > 0
      ? {
          ...BookingParamsTransformer.decode(Object.fromEntries(query.entries())),
          ...params
        }
      : { ...params };
  }, [getSearchParams, query.toString()]);

  function generateBookingDetailsLink(bookingId: string | undefined): string {
    if (bookingDetailsLink && bookingId) {
      return `${getLinkUrl(bookingDetailsLink, config)}?bookingId=${bookingId}`;
    }
    return '';
  }

  function getAgentNameInfo(bookingPane: ApiBookingPane): string {
    if (bookingPane.agent?.lastName && bookingPane.agent?.firstName) {
      return `${bookingPane.agent?.lastName} ${bookingPane.agent?.firstName}`;
    }
    return '-/-';
  }

  function redirectToDetailsPage(bookingId: string | undefined) {
    router.push(generateBookingDetailsLink(bookingId));
  }

  const handleBookingPaneClick = (bookingId: string | undefined): void => {
    if (bookingId) {
      router.push(`${getLinkUrl(bookingDetailsLink, config)}?bookingId=${bookingId}`);
    }
  };

  function renderCustomBookingRow(bookingPane: ApiBookingPane): ReactNode {
    return (
      <tbody key={bookingPane.bookingNumber}>
        <tr
          className="iso__bookingList__row"
          onClick={(): void => redirectToDetailsPage(bookingPane.bookingId)}
        >
          <td>{bookingPane.bookingNumber}</td>
          <td>{t(`${bookingPane.bookingState}`)}</td>
          <td>
            <DateDisplay date={bookingPane.bookingDate as string} />
          </td>
          <td>
            <DateDisplay date={bookingPane.travelStartDate as string} />
          </td>
          <td>
            {bookingPane.price?.finalPrice} {bookingPane.price?.currencyCode}
          </td>
          <td>{getAgentNameInfo(bookingPane)}</td>
          <td>{bookingPane.leadTravelerName}</td>
        </tr>
      </tbody>
    );
  }

  return (
    <>
      {isLoggedIn && (
        <BookingSearchModule
          onSearch={updateUrlParams}
          searchParams={searchParams}
          handleBookingPaneClick={handleBookingPaneClick}
          customBookingRecordRenderer={renderCustomBookingRow}
          showLeadTravelName
        />
      )}
    </>
  );
};

export default BookingList;
